import { z } from 'zod/v4';

/** One page of orders, the largest page the lists serve. */
export const ORDERS_READINESS_MAX_ORDERS = 100;

export const OrdersReadinessInputSchema = z.object({
  orderIds: z.array(z.string().min(1)).min(1).max(ORDERS_READINESS_MAX_ORDERS),
  /**
   * Also return each line's verdict. Off by default: the orders list needs only
   * the order verdict, and an order can carry hundreds of lines.
   */
  includeLines: z.boolean().optional(),
});

export type OrdersReadinessInput = z.infer<typeof OrdersReadinessInputSchema>;

/**
 * The send cell's state for one order and one workflow.
 *
 * The six agreed states are `archived`, `sent`, `partialReady`,
 * `partialBlocked`, `missing` and `ready`. `notApplicable` (the send refuses
 * the order outright), `internalOrder` and `referenced` (Tracing only) sit
 * outside them. `referenced`: every line is covered, by another company's
 * Tracing request rather than a send of this one's. An order with no lines is
 * `missing` with every count at zero.
 */
export const ORDER_SEND_STATE_VALUES = [
  'notApplicable',
  'internalOrder',
  'archived',
  'sent',
  'referenced',
  'partialReady',
  'partialBlocked',
  'missing',
  'ready',
] as const;

export type OrderSendState = (typeof ORDER_SEND_STATE_VALUES)[number];

export const LINE_SEND_STATE_VALUES = [
  'notApplicable',
  'internalOrder',
  'archived',
  'sent',
  'referenced',
  'ready',
  'blocked',
] as const;

export type LineSendState = (typeof LINE_SEND_STATE_VALUES)[number];

export const OrderWorkflowReadinessSchema = z.object({
  state: z.enum(ORDER_SEND_STATE_VALUES),
  /** Lines covered: sent, or (Tracing) referenced to another company's request. */
  sentCount: z.number().int(),
  /** Unsent lines the send would take. */
  readyCount: z.number().int(),
  /** Unsent lines the send would skip. */
  blockedCount: z.number().int(),
  /**
   * Why the blocked lines are blocked, across all of them, in seed order. The
   * send's own reason keys: readiness check keys plus
   * `missing_order_line_number` and `duplicate_order_line_number`.
   */
  missingChecks: z.array(z.string()),
  /** Required checks that could not be evaluated. They never block. */
  unknownChecks: z.array(z.string()),
  /** The latest send of any line; `null` when nothing was sent. */
  lastSentAt: z.string().nullable(),
  /** Lines were sent on more than one day. */
  multipleDates: z.boolean(),
  /**
   * CMM only: the first few blocked lines and what each one misses, so the
   * cell can say which line holds the order back. `blockedCount` has them all.
   */
  blockedLines: z
    .array(
      z.object({
        orderLineNumber: z.string().nullable(),
        missingChecks: z.array(z.string()),
      }),
    )
    .optional(),
});

export type OrderWorkflowReadiness = z.infer<
  typeof OrderWorkflowReadinessSchema
>;

export const LineWorkflowReadinessSchema = z.object({
  state: z.enum(LINE_SEND_STATE_VALUES),
  missingChecks: z.array(z.string()),
  sentAt: z.string().nullable(),
});

export type LineWorkflowReadiness = z.infer<typeof LineWorkflowReadinessSchema>;

export const OrderReadinessSchema = z.object({
  tracing: OrderWorkflowReadinessSchema,
  /** `null` while the CMM integration is off for the tenant. */
  cmm: OrderWorkflowReadinessSchema.nullable(),
  /** Present when `includeLines` was asked for, keyed by order line id. */
  lines: z
    .record(
      z.string(),
      z.object({
        tracing: LineWorkflowReadinessSchema,
        cmm: LineWorkflowReadinessSchema.nullable(),
      }),
    )
    .optional(),
});

export type OrderReadiness = z.infer<typeof OrderReadinessSchema>;

/** Keyed by order id. Ids the caller cannot see are absent. */
export const OrdersReadinessResponseSchema = z.record(
  z.string(),
  OrderReadinessSchema,
);

export type OrdersReadinessResponse = z.infer<
  typeof OrdersReadinessResponseSchema
>;
